import styled from "@emotion/styled";
import { Dispatch, useCallback } from "react";

import { useDeleteAttachmentMutation } from "@/queries/attachments/mutations";
import { Attachment, PublicQuestion } from "@/queries/types";
import { useRouter } from "next/router";
import { AttachmentBubble } from "../attachment-bubble";
import { Carousel, useCarousel } from "../carousel";
import { Action } from "./reducer";

interface QuestionAttachmentsProps {
  attachments: Record<string, Attachment[]>;
  question?: PublicQuestion;
  dispatch: Dispatch<Action>;
}

export function QuestionAttachments({
  attachments,
  question,
  dispatch,
}: QuestionAttachmentsProps) {
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const {
    containerRef,
    contentRef,
    goNext,
    goPrev,
    canGoNext,
    canGoPrev,
    offset,
  } = useCarousel({ slideStep: 143 });

  const deleteAttachmentMutation = useDeleteAttachmentMutation();

  const handleDelete = useCallback(
    async (attachment: Attachment) => {
      if (question) {
        deleteAttachmentMutation.mutate(
          {
            job_opening_slug: jobOpeningSlug,
            attachment_id: attachment.id,
          },
          {
            onSuccess: () => {
              dispatch({
                type: "delete_answer_attachment",
                payload: {
                  questionId: question.test_question_id.toString(),
                  attachmentId: attachment.id,
                },
              });
            },
          },
        );
      }
    },
    [dispatch, question, deleteAttachmentMutation, jobOpeningSlug],
  );

  if (!question) {
    return null;
  }

  const questionAttachments = attachments[question.test_question_id];

  return (
    <Wrapper>
      <Container ref={containerRef}>
        <Content style={offset} ref={contentRef}>
          {questionAttachments?.map((attachment) => (
            <AttachmentBubble
              key={attachment.id}
              label={attachment.original_file_name}
              onDelete={() => handleDelete(attachment)}
            />
          ))}
        </Content>
      </Container>
      {(canGoPrev || canGoNext) && (
        <Carousel
          canGoToPrev={canGoPrev}
          canGoToNext={canGoNext}
          goPrev={goPrev}
          goNext={goNext}
          width="48px"
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  position: relative;
  transition: left 0.2s;
`;
